import { useConfirmationDialog } from "@components/dialog/withConfirmationDialog";
import { CopyIcon } from "@components/icon";
import { getHourDifference, isValidDate } from "@components/inputs/date/utils";
import { IFieldDef } from "@components/smart/FieldInfo";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import {
    IPrWorkingPatternDayEntity,
    IPrWorkingPatternDayIntervalEntity,
    IPrWorkingPatternEntity,
    PrWorkingPatternDayEntity,
    PrWorkingPatternDayIntervalEntity
} from "@odata/GeneratedEntityTypes";
import { PrWorkingPatternIntervalTypeCode } from "@odata/GeneratedEnums";
import { getHoursFromTimeStamp } from "@pages/payroll/attendance/Attendance.utils";
import {
    getIntervalIndex,
    getTimeSpanFromHours,
    normalizeIntervalsAfterChange,
    validateInterval,
    validateIntervals
} from "@pages/payroll/workingPatterns/WorkDayIntervals.utils";
import { isDefined, roundToDecimalPlaces } from "@utils/general";
import { cloneDeep } from "lodash";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/button";
import SmartFastEntryList, {
    ActionType,
    ISmartFastEntriesActionEvent
} from "../../../components/smart/smartFastEntryList";
import BindingContext from "../../../odata/BindingContext";
import { getUtcDayjs } from "../../../types/Date";
import NumberType from "../../../types/Number";
import { FormStorage } from "../../../views/formView/FormStorage";
import { WorkDayIntervalsFastEntryHeader, WorkDayIntervalsFastEntryWrapper } from "./WorkingPattern.styles";
import { recalculateWeeklyWorkingHours, WORKING_HOURS_DECIMAL_PLACES } from "./WorkingPatterns.utils";
import { timeDiffPath } from "./WorkingPatternsDef";
import { IWorkingPatternCustomData } from "./WorkingPatternsFormView";

interface IProps {
    storage: FormStorage<IPrWorkingPatternEntity, IWorkingPatternCustomData>;
    isReadOnly?: boolean;
}

const WorkDayIntervalsFastEntry: React.FC<IProps> = ({
                                                         storage,
                                                         isReadOnly
                                                     }) => {
    const { t } = useTranslation(["WorkingPatterns", "Components"]);
    const confirmationDialog = useConfirmationDialog();

    const selectedDayBc: BindingContext = storage.getCustomData().selectedDay;
    const selectedDay: IPrWorkingPatternDayEntity = isDefined(selectedDayBc) ? storage.getValue(selectedDayBc) : null;

    const columns: IFieldDef[] = [
        { id: PrWorkingPatternDayIntervalEntity.Type },
        { id: PrWorkingPatternDayIntervalEntity.TimeStart },
        { id: PrWorkingPatternDayIntervalEntity.TimeEnd },
        { id: timeDiffPath }
    ];

    const recalculateWorkingHours = useCallback((): void => {
        const workingHours = selectedDay?.Intervals?.filter(i => {
            return isValidDate(i.TimeStart) && isValidDate(i.TimeEnd) && i.Type?.Code === PrWorkingPatternIntervalTypeCode.Work;
        })?.reduce((acc, i) => {
            const timeDiff = getHourDifference(getUtcDayjs(i.TimeStart), getUtcDayjs(i.TimeEnd), i.IsEndNextDay !== i.IsStartNextDay);
            return acc + (isNaN(timeDiff) || timeDiff < 0 ? 0 : timeDiff); // do not count negative intervals -> invalid
        }, 0) ?? 0;
        storage.setValue(selectedDayBc.navigate(PrWorkingPatternDayEntity.WorkingHours), getTimeSpanFromHours(roundToDecimalPlaces(WORKING_HOURS_DECIMAL_PLACES, workingHours)));
        recalculateWeeklyWorkingHours(storage);
        storage.refresh();
    }, [storage, selectedDay?.Intervals, selectedDayBc]);


    const handleChange = (e: ISmartFieldChange): void => {
        storage.handleLineItemsChange(e);
        storage.refreshFields();
        const path = e.bindingContext.getPath();

        if (path === PrWorkingPatternDayIntervalEntity.Type) {
            const intervalBc = e.bindingContext.getParent();
            const interval = storage.getValue(intervalBc);
            validateInterval(interval, intervalBc, storage);
        }

        if (path === PrWorkingPatternDayIntervalEntity.Type || isValidDate(e.value as Date)) {
            recalculateWorkingHours();
            // refresh local context field with hours diff
            storage.refresh();
        }
    };

    const handleBlur = useCallback(async (e: ISmartFieldBlur): Promise<void> => {
        const path = e.bindingContext.getPath();
        const intervalBc = e.bindingContext.getParent();
        const intervalsBc = intervalBc.removeKey();
        const isStart = path === PrWorkingPatternDayIntervalEntity.TimeStart;
        const isEnd = path === PrWorkingPatternDayIntervalEntity.TimeEnd;
        let refreshPage = false;
        const intervals = storage.getValue(intervalsBc) as IPrWorkingPatternDayIntervalEntity[];
        const currIdx = getIntervalIndex(intervals, intervalBc);

        if (isStart || isEnd) {
            normalizeIntervalsAfterChange(intervals, isStart ? "start" : "end", currIdx);
            storage.setValue(intervalsBc, intervals);
            validateIntervals(intervals, storage, intervalsBc);
            refreshPage = true;
        }

        storage.handleBlur(e);
        recalculateWorkingHours();
        storage.refresh(refreshPage);
    }, [storage, recalculateWorkingHours]);

    const handleClone = useCallback(async (): Promise<void> => {
        const isConfirmed = await confirmationDialog.open({
            content: t("WorkingPatterns:ConfirmationText", { hours: getHoursFromTimeStamp(selectedDay.WorkingHours) })
        });

        if (isConfirmed) {
            const entity = storage.data.entity as IPrWorkingPatternEntity;
            const daysWithSameWH = entity.Days.filter(d => (getHoursFromTimeStamp(d.WorkingHours) === getHoursFromTimeStamp(selectedDay.WorkingHours)));
            for (const day of daysWithSameWH) {
                day.Intervals = cloneDeep(selectedDay.Intervals);
            }
            storage.refresh();
        }
    }, [storage, selectedDay, confirmationDialog, t]);

    const handleAction = useCallback((e: ISmartFastEntriesActionEvent<IPrWorkingPatternDayIntervalEntity>) => {
        const intervalsBc = e.bindingContext.removeKey();
        const origIntervals = storage.getValue(intervalsBc);
        storage.handleLineItemsAction(e);
        storage.refresh();

        if (e.actionType === ActionType.Remove) {
            const currentInterval = e.affectedItems?.[0];
            const intervals = storage.getValue(intervalsBc);
            const currIdx = getIntervalIndex(origIntervals, intervalsBc.addKey(currentInterval));
            normalizeIntervalsAfterChange(intervals, "delete", currIdx);
            storage.setValue(intervalsBc, intervals);
        }

        recalculateWorkingHours();
    }, [storage, recalculateWorkingHours]);

    if (!selectedDay) {
        return null;
    }

    return <WorkDayIntervalsFastEntryWrapper>
        <WorkDayIntervalsFastEntryHeader>
            <b>{`${t("WorkingPatterns:Interval")} ${NumberType.format(getHoursFromTimeStamp(selectedDay.WorkingHours) ?? 0)} ${t("Components:Calendar.HourPlaceholder")}`}</b>
            <Button
                title={t("WorkingPatterns:UseForAll")}
                isDisabled={isReadOnly}
                isDecorative
                onClick={handleClone}
                icon={<CopyIcon/>}
            />
        </WorkDayIntervalsFastEntryHeader>
        <SmartFastEntryList
            storage={storage}
            bindingContext={selectedDayBc.navigate(PrWorkingPatternDayEntity.Intervals)}
            columns={columns}
            onChange={handleChange}
            onBlur={handleBlur}
            useLabelWrapping={true}
            canAdd={true}
            isItemCloneable={false}
            isItemRemovable={!isReadOnly}
            isAddButtonTransparent={true}
            onAction={handleAction}
            order={PrWorkingPatternDayIntervalEntity.Order}
            isReadOnly={isReadOnly}
            renderAsFastEntryForReadOnly
        />
    </WorkDayIntervalsFastEntryWrapper>;
};

export default WorkDayIntervalsFastEntry;
